/**
 * usePartyDialog – private helpers
 *
 * Module-scoped helpers extracted from usePartyDialog.ts so the main hook
 * file stays under the max-lines/complexity limits. Not exported from the
 * public barrel (./index.ts).
 */

import type { Party, CreatePartyRequest } from '@/gen/proto';

import { EMPTY_PENDING_BANK, type PartyFormData } from './party-dialog-helpers';
import { listAccounts, createAccount } from '../../../services/account-service';
import {
  createEntity,
  searchByName,
  type Entity,
  type EntityAddress,
  type BusinessIdentifier,
} from '../../../services/entity-service';
import {
  getCountries,
  getCountryByCode,
} from '../../../services/geolocation-service';
import {
  createParty,
  updateParty,
  type UpdatePartyRequest,
} from '../../../services/party-service';
import { ROLE_CUSTOMER, ROLE_SUPPLIER } from '../../../types/billing';
import { rolesToPartyType } from '../../../utils/billing-helpers';

import type { Toast } from '../../ui';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CountryIdentifier {
  key: string;
  label: string;
  placeholder: string;
  maxLength: number | undefined;
  isRequired: boolean;
}

// ---------------------------------------------------------------------------
// flushPendingForms – split into address/bank helpers to keep complexity low
// ---------------------------------------------------------------------------

function isPendingAddressReady(data: PartyFormData): boolean {
  if (!data.pendingAddressOpen || !data.pendingAddress) { return false; }
  const p = data.pendingAddress;
  return Boolean(p.line1?.trim() && p.city?.trim() && p.postalCode?.trim());
}

function buildAddressFromPending(data: PartyFormData): EntityAddress {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- guarded by isPendingAddressReady
  const p = data.pendingAddress!;
  return {
    label: p.usage[0] ?? 'other',
    type: p.type,
    usage: p.usage.length > 0 ? p.usage : undefined,
    line1: p.line1.trim(),
    line2: p.line2.trim() || undefined,
    city: p.city.trim(),
    state: p.state || undefined,
    stateCode: p.stateCode || undefined,
    postalCode: p.postalCode.trim(),
    country: p.country,
    countryCode: p.countryCode,
    gstin: p.gstin.trim() || undefined,
    isPrimary: data.addresses.length === 0,
  };
}

function buildBankFromPending(data: PartyFormData): NonNullable<PartyFormData['bankAccounts'][number]> {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- guarded by caller
  const pb = data.pendingBankAccount!;
  return {
    accountName: pb.accountName.trim(),
    bankName: pb.bankName.trim() || undefined,
    accountNumber: pb.accountNumber.trim() || undefined,
    ifscCode: pb.ifscCode.trim() ? pb.ifscCode.trim().toUpperCase() : undefined,
    branch: pb.branch.trim() || undefined,
    swiftCode: pb.swiftCode.trim() ? pb.swiftCode.trim().toUpperCase() : undefined,
    iban: pb.iban.trim() ? pb.iban.trim().toUpperCase() : undefined,
    currency: (pb.currency.trim() || 'INR').toUpperCase(),
    accountType: 'current' as const,
  };
}

export function flushPendingForms(formData: PartyFormData): PartyFormData {
  let result = formData;

  if (isPendingAddressReady(result)) {
    const addr = buildAddressFromPending(result);
    result = { ...result, addresses: [...result.addresses, addr], pendingAddress: null, pendingAddressOpen: false };
  }

  if (result.pendingBankAccount?.accountName?.trim()) {
    const ba = buildBankFromPending(result);
    result = { ...result, bankAccounts: [...result.bankAccounts, ba], pendingBankAccount: EMPTY_PENDING_BANK };
  }

  return result;
}

// ---------------------------------------------------------------------------
// Title / dedup / parties map
// ---------------------------------------------------------------------------

export function computeTitleFromRoles(roles: string[]): string {
  if (roles.length === 1 && roles[0] === ROLE_CUSTOMER) { return 'Customer'; }
  if (roles.length === 1 && roles[0] === ROLE_SUPPLIER) { return 'Vendor'; }
  return 'Party';
}

export function deduplicateEntities(
  nameResults: Entity[],
  identifierResults: Entity[],
): Entity[] {
  const seen = new Set<string>();
  const all: Entity[] = [];
  for (const e of [...nameResults, ...identifierResults]) {
    if (!seen.has(e.id)) { seen.add(e.id); all.push(e); }
  }
  return all;
}

export async function buildPartiesMap(
  entities: Entity[],
  fetchFn: (id: string) => Promise<Party[]>,
): Promise<Map<string, Party[]>> {
  const map = new Map<string, Party[]>();
  await Promise.all(entities.map(async e => {
    const parties = await fetchFn(e.id);
    if (parties.length > 0) { map.set(e.id, parties); }
  }));
  return map;
}

// ---------------------------------------------------------------------------
// Address country resolution
// ---------------------------------------------------------------------------

async function resolveCountryByName(name: string): Promise<{ code: string; name: string }> {
  const all = await getCountries();
  const match = all.find(c => c.name.toLowerCase() === name.toLowerCase());
  if (match) { return { code: match.identifier, name: match.name }; }
  return { code: 'IN', name };
}

async function resolveCountryDefault(): Promise<{ code: string; name: string }> {
  const c = await getCountryByCode('IN');
  return { code: 'IN', name: c?.name ?? 'India' };
}

export async function resolveAddressCountry(
  addr: EntityAddress | undefined,
): Promise<Partial<import('@/gen/proto').Address> | undefined> {
  if (!addr) { return undefined; }

  let countryCode = addr.countryCode;
  let countryName = addr.country;

  if (countryCode) {
    const c = await getCountryByCode(countryCode);
    if (c) { countryName = c.name; }
  } else if (countryName) {
    const resolved = await resolveCountryByName(countryName);
    countryCode = resolved.code;
    countryName = resolved.name;
  } else {
    const resolved = await resolveCountryDefault();
    countryCode = resolved.code;
    countryName = resolved.name;
  }

  return {
    line1: addr.line1,
    line2: addr.line2,
    city: addr.city,
    state: addr.state,
    stateCode: addr.stateCode ?? '',
    postalCode: addr.postalCode,
    country: countryName ?? 'India',
    countryCode,
  };
}

// ---------------------------------------------------------------------------
// Entity / address ID helpers
// ---------------------------------------------------------------------------

export function resolveEntityId(
  editingParty: Party | null | undefined,
  formData: PartyFormData,
  selectedEntity: Entity | null,
): string | undefined {
  if (editingParty?.entityId) { return String(editingParty.entityId); }
  if (formData.entityId) { return String(formData.entityId); }
  if (selectedEntity?.id) { return String(selectedEntity.id); }
  return undefined;
}

function isBillingAddress(addr: EntityAddress): boolean {
  return (addr.usage?.includes('billing') ?? false) || addr.label === 'billing';
}

function isShippingAddress(addr: EntityAddress): boolean {
  return (addr.usage?.includes('shipping') ?? false) || addr.label === 'shipping';
}

export function extractAddressIdsFromList(addresses: EntityAddress[]): { billing?: string; shipping?: string } {
  let billing: string | undefined;
  let shipping: string | undefined;
  for (const addr of addresses) {
    if (isBillingAddress(addr) && addr.id) { billing = String(addr.id); }
    if (isShippingAddress(addr) && addr.id) { shipping = String(addr.id); }
  }
  return { billing, shipping };
}

// ---------------------------------------------------------------------------
// Build payloads
// ---------------------------------------------------------------------------

export function buildBusinessIdentifiers(
  formData: PartyFormData,
  countryIdentifiers: CountryIdentifier[],
): BusinessIdentifier[] {
  const result: BusinessIdentifier[] = [];
  for (const identifier of countryIdentifiers) {
    const value = (formData as unknown as Record<string, unknown>)[identifier.key];
    if (value && typeof value === 'string' && value.trim().length > 0) {
      result.push({ type: identifier.key, value: value.trim() });
    }
  }
  return result;
}

export function buildEntityAddresses(formData: PartyFormData): EntityAddress[] {
  return formData.addresses;
}

export function buildContactHandles(
  formData: PartyFormData,
): Array<{ type: string; value: string; is_primary?: boolean }> {
  const handles: Array<{ type: string; value: string; is_primary?: boolean }> = [];
  if (formData.email?.trim()) { handles.push({ type: 'email', value: formData.email.trim(), is_primary: true }); }
  if (formData.phone?.full?.trim()) { handles.push({ type: 'phone', value: formData.phone.full.trim() }); }
  if (formData.mobile?.full?.trim()) { handles.push({ type: 'mobile', value: formData.mobile.full.trim() }); }
  return handles;
}

// ---------------------------------------------------------------------------
// createNewEntity – break out the address-id extraction
// ---------------------------------------------------------------------------

function primaryCountryCodeOf(formData: PartyFormData, addresses: EntityAddress[]): string {
  return addresses.find(isBillingAddress)?.countryCode
    ?? addresses[0]?.countryCode
    ?? formData.billingAddress.countryCode
    ?? 'IN';
}

function extractNewEntityAddressIds(
  addresses: ReadonlyArray<EntityAddress> | undefined,
): { billing?: string; shipping?: string } {
  if (!addresses) { return {}; }
  let billing: string | undefined;
  let shipping: string | undefined;
  for (const addr of addresses) {
    if (isBillingAddress(addr) && addr.id) { billing = String(addr.id); }
    if (isShippingAddress(addr) && addr.id) { shipping = String(addr.id); }
  }
  return { billing, shipping };
}

export async function createNewEntity(
  formData: PartyFormData,
  countryIdentifiers: CountryIdentifier[],
): Promise<{ entityId?: string; billingAddressId?: string; shippingAddressId?: string }> {
  const businessIdentifiers = buildBusinessIdentifiers(formData, countryIdentifiers);
  const contactHandles = buildContactHandles(formData);
  const addresses = buildEntityAddresses(formData);
  const tags = [...(formData.roles ?? [])];

  const result = await createEntity({
    name: formData.entityName,
    countryCode: primaryCountryCodeOf(formData, addresses),
    businessIdentifiers,
    contactHandles,
    addresses: addresses.length > 0 ? addresses : undefined,
    bankAccounts: formData.bankAccounts.length > 0 ? formData.bankAccounts : undefined,
    tags,
    status: 'active',
  });

  if (!result.data) { throw new Error(result.error?.message ?? 'Failed to create entity'); }

  const ids = extractNewEntityAddressIds(result.data.addresses);
  return {
    entityId: String(result.data.id),
    billingAddressId: ids.billing,
    shippingAddressId: ids.shipping,
  };
}

// ---------------------------------------------------------------------------
// createPartyGLAccounts – split per-role to keep complexity low
// ---------------------------------------------------------------------------

type GLAccountIds = { receivableAccountId?: string; payableAccountId?: string };

async function createReceivableAccount(
  orgId: string,
  partyCode: string,
  assets: Array<{ id: string; code: string; isControl?: boolean; accountSubtype?: string }>,
): Promise<string | undefined> {
  const arParent = assets.find(a => a.isControl && a.accountSubtype === 'Accounts Receivable');
  if (!arParent) { return undefined; }
  const res = await createAccount(orgId, {
    code: `${arParent.code}-${partyCode}`,
    name: `${partyCode} – Receivable`,
    accountType: 'ACCOUNT_TYPE_ASSET',
    accountSubtype: 'Accounts Receivable',
    parentId: arParent.id,
    idempotencyKey: `gl-ar-${orgId}-${partyCode}`,
  });
  return res.data?.id;
}

async function createPayableAccount(
  orgId: string,
  partyCode: string,
  liabilities: Array<{ id: string; code: string; isControl?: boolean; accountSubtype?: string }>,
): Promise<string | undefined> {
  const apParent = liabilities.find(a => a.isControl && a.accountSubtype === 'Accounts Payable');
  if (!apParent) { return undefined; }
  const res = await createAccount(orgId, {
    code: `${apParent.code}-${partyCode}`,
    name: `${partyCode} – Payable`,
    accountType: 'ACCOUNT_TYPE_LIABILITY',
    accountSubtype: 'Accounts Payable',
    parentId: apParent.id,
    idempotencyKey: `gl-ap-${orgId}-${partyCode}`,
  });
  return res.data?.id;
}

export async function createPartyGLAccounts(
  orgId: string,
  partyCode: string,
  roles: string[],
): Promise<GLAccountIds> {
  const isCustomer = roles.includes(ROLE_CUSTOMER);
  const isSupplier = roles.includes(ROLE_SUPPLIER);
  if (!isCustomer && !isSupplier) { return {}; }

  const [assetResult, liabilityResult] = await Promise.all([
    listAccounts(orgId, { accountType: 'ACCOUNT_TYPE_ASSET', includeHeaders: true }, 500),
    listAccounts(orgId, { accountType: 'ACCOUNT_TYPE_LIABILITY', includeHeaders: true }, 500),
  ]);

  const allAccounts = [
    ...(assetResult.data?.accounts ?? []),
    ...(liabilityResult.data?.accounts ?? []),
  ];

  const out: GLAccountIds = {};
  if (isCustomer) {
    out.receivableAccountId = await createReceivableAccount(orgId, partyCode, allAccounts);
  }
  if (isSupplier) {
    out.payableAccountId = await createPayableAccount(orgId, partyCode, allAccounts);
  }
  return out;
}

// ---------------------------------------------------------------------------
// savePartyCreate / savePartyUpdate – options-object signatures
// ---------------------------------------------------------------------------

export interface SavePartyCreateOptions {
  orgId: string;
  ledgerId: string;
  formData: PartyFormData;
  entityId: string;
  billingAddressId?: string;
  shippingAddressId?: string;
  countryIdentifiers: CountryIdentifier[];
}

function buildContactPayload(formData: PartyFormData): {
  email: string; phone: string; mobile: string; website: string;
} {
  return {
    email: formData.email ?? '',
    phone: formData.phone?.full ?? '',
    mobile: formData.mobile?.full ?? '',
    website: formData.website ?? '',
  };
}

export async function savePartyCreate(options: SavePartyCreateOptions): Promise<Party> {
  const { orgId, ledgerId, formData, entityId, billingAddressId, shippingAddressId, countryIdentifiers } = options;
  const businessIdentifiers = buildBusinessIdentifiers(formData, countryIdentifiers);
  const billingAddr = formData.addresses.find(isBillingAddress);
  const shippingAddr = formData.addresses.find(isShippingAddress);

  // Auto-create GL sub-ledger accounts for customer/supplier roles (best-effort)
  const glAccounts = await createPartyGLAccounts(orgId, formData.code, formData.roles)
    .catch((): GLAccountIds => ({}));

  const createData: Partial<CreatePartyRequest> = {
    entityId,
    code: formData.code,
    name: formData.entityName,
    tradeName: formData.tradeName ?? undefined,
    partyType: rolesToPartyType(formData.roles),
    roles: formData.roles,
    gstin: formData.gstin ?? undefined,
    gstRegistrationType: formData.gstRegistrationType,
    contact: buildContactPayload(formData),
    businessIdentifiers,
    billingAddress: billingAddr ? billingAddr as unknown as import('@/gen/proto').Address : undefined,
    shippingAddress: shippingAddr ? shippingAddr as unknown as import('@/gen/proto').Address : undefined,
    billingAddressId: billingAddressId ?? undefined,
    shippingAddressId: shippingAddressId ?? undefined,
    creditDays: formData.creditDays,
    creditLimit: String(formData.creditLimit),
    defaultCurrency: formData.defaultCurrency,
    companyVendorCode: formData.companyVendorCode ?? undefined,
    receivableAccountId: glAccounts.receivableAccountId,
    payableAccountId: glAccounts.payableAccountId,
  };

  const result = await createParty(orgId, ledgerId, createData as CreatePartyRequest);
  if (!result.data) { throw new Error(result.error?.message ?? 'Failed to create party'); }
  return result.data;
}

export interface SavePartyUpdateOptions {
  orgId: string;
  editingParty: Party;
  formData: PartyFormData;
  entityId: string;
  billingAddressId?: string;
  shippingAddressId?: string;
  countryIdentifiers: CountryIdentifier[];
}

export async function savePartyUpdate(options: SavePartyUpdateOptions): Promise<Party> {
  const { orgId, editingParty, formData, entityId, billingAddressId, shippingAddressId, countryIdentifiers } = options;
  const updateAddresses = buildEntityAddresses(formData)
    .map(addr => ({ ...addr } as import('@/gen/proto').Address));

  const updateBusinessIdentifiers = buildBusinessIdentifiers(formData, countryIdentifiers);

  const updateData = {
    entityId: entityId ?? undefined,
    name: formData.entityName,
    tradeName: formData.tradeName ?? undefined,
    gstin: formData.gstin ?? undefined,
    gstRegistrationType: formData.gstRegistrationType,
    businessIdentifiers: updateBusinessIdentifiers.length > 0 ? updateBusinessIdentifiers : undefined,
    contact: buildContactPayload(formData),
    addresses: updateAddresses,
    billingAddressId: billingAddressId ?? undefined,
    shippingAddressId: shippingAddressId ?? undefined,
    creditDays: formData.creditDays,
    creditLimit: String(formData.creditLimit),
    // Set-once: send currency only when the party has none, so a legacy
    // currency-less party can be fixed while an established currency stays
    // immutable. The server enforces this too as a backstop.
    defaultCurrency: editingParty.defaultCurrency ? undefined : formData.defaultCurrency,
    companyVendorCode: formData.companyVendorCode,
    isActive: true,
  } as unknown as UpdatePartyRequest;

  const result = await updateParty(orgId, editingParty.id, updateData);
  if (!result.data) { throw new Error(result.error?.message ?? 'Failed to update party'); }
  return result.data;
}

// ---------------------------------------------------------------------------
// handleSaveError – split parsing from side-effects
// ---------------------------------------------------------------------------

function extractDuplicateEntityNames(errorMsg: string): Set<string> {
  const entityNameMatches = errorMsg.matchAll(/already used by entity: ([^)]+)\)/g);
  const entityNames = new Set<string>();
  for (const match of entityNameMatches) {
    if (match[1]) { entityNames.add(match[1].trim()); }
  }
  return entityNames;
}

async function lookupDuplicateEntities(entityNames: Set<string>): Promise<Entity[]> {
  const results = await Promise.all(
    Array.from(entityNames).map(name => searchByName(name)),
  );
  const foundEntities: Entity[] = [];
  for (const result of results) {
    if (result.data && Array.isArray(result.data)) { foundEntities.push(...result.data); }
  }
  return Array.from(new Map(foundEntities.map(e => [e.id, e])).values());
}

export interface HandleSaveErrorOptions {
  error: unknown;
  fetchPartiesForEntity: (id: string) => Promise<Party[]>;
  setDuplicateEntities: (entities: Entity[]) => void;
  setDuplicateEntitiesParties: (map: Map<string, Party[]>) => void;
  setShowDuplicateWarning: (v: boolean) => void;
  addToast: (opts: Omit<Toast, 'id'>) => void;
}

async function tryHandleDuplicateError(options: HandleSaveErrorOptions, errorMsg: string): Promise<boolean> {
  const { fetchPartiesForEntity, setDuplicateEntities, setDuplicateEntitiesParties, setShowDuplicateWarning, addToast } = options;
  const entityNames = extractDuplicateEntityNames(errorMsg);
  if (entityNames.size === 0) { return false; }

  try {
    const uniqueEntities = await lookupDuplicateEntities(entityNames);
    if (uniqueEntities.length === 0) { return false; }

    const partiesMap = await buildPartiesMap(uniqueEntities, fetchPartiesForEntity);
    setDuplicateEntities(uniqueEntities);
    setDuplicateEntitiesParties(partiesMap);
    setShowDuplicateWarning(true);
    addToast({
      variant: 'warning',
      message: 'Duplicate business identifiers found. Please link to an existing entity or edit the identifiers.',
    });
    return true;
  } catch {
    return false;
  }
}

export async function handleSaveError(options: HandleSaveErrorOptions): Promise<void> {
  const { error, addToast } = options;
  const errorMsg = error instanceof Error ? error.message : '';

  if (errorMsg.includes('Duplicate business identifiers found')) {
    const didHandle = await tryHandleDuplicateError(options, errorMsg);
    if (didHandle) { return; }
  }

  addToast({
    variant: 'error',
    message: (error instanceof Error ? error.message : undefined) ?? 'Failed to save party. Please try again.',
  });
}
